(() => {
  const stateStore = (window.MinimogCollectionSearchState = window.MinimogCollectionSearchState || {});

  class CollectionSearch extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";

      this.section = this.closest(".facest-filters-section");
      this.sectionId = this.dataset.sectionId || this.section?.dataset.sectionId;
      this.storageKey = this.dataset.storageKey || this.sectionId || window.location.pathname;
      this.minCharacters = Math.max(1, Number(this.dataset.minCharacters) || 2);
      this.mobileSlot = this.section?.querySelector("[data-collection-search-mobile-slot]");
      this.desktopSlot = this.section?.querySelector("[data-collection-search-desktop-slot]");

      this.form = this.querySelector("[data-collection-search-form]");
      this.input = this.querySelector("[data-collection-search-input]");
      this.clearButton = this.querySelector("[data-collection-search-clear]");
      this.submitButton = this.querySelector("[data-collection-search-submit]");
      this.status = this.querySelector("[data-collection-search-status]");
      this.grid = this.section?.querySelector("[data-product-container]");
      this.pagination = this.section?.querySelector(".m-collection--pagination");
      this.emptyState = this.section?.querySelector("[data-collection-search-empty]");

      if (!this.form || !this.input) return;

      this.currentPage = Number(new URL(window.location.href).searchParams.get("page")) || 1;
      this.totalPages = Number(this.grid?.dataset.totalPages) || 1;
      this.initialItems = this.grid
        ? Array.from(this.grid.querySelectorAll("[data-collection-search-item]")).map((item) => item.cloneNode(true))
        : [];
      this.allItems = [...this.initialItems];
      this.allItemsPromise = null;
      this.requestId = 0;
      this.inputTimer = null;

      this.form.addEventListener("submit", this.handleSubmit);
      this.input.addEventListener("input", this.handleInput);
      this.clearButton?.addEventListener("click", this.handleClear);

      this.setupResponsivePlacement();

      const savedQuery = stateStore[this.storageKey] || "";
      if (savedQuery) {
        this.input.value = savedQuery;
        this.toggleClearButton(true);
        window.requestAnimationFrame(() => this.search(savedQuery));
      }
    }

    disconnectedCallback() {
      window.clearTimeout(this.inputTimer);

      // Moving the same custom element between the mobile and desktop slots
      // briefly triggers disconnectedCallback. Defer cleanup so responsive
      // movement does not remove the media-query listener.
      window.setTimeout(() => {
        if (this.isConnected || !this.desktopMediaQuery) return;

        if (typeof this.desktopMediaQuery.removeEventListener === "function") {
          this.desktopMediaQuery.removeEventListener("change", this.updatePlacement);
        } else if (typeof this.desktopMediaQuery.removeListener === "function") {
          this.desktopMediaQuery.removeListener(this.updatePlacement);
        }
      }, 0);
    }

    setupResponsivePlacement() {
      if (!this.mobileSlot || !this.desktopSlot) return;

      this.desktopMediaQuery = window.matchMedia("(min-width: 768px)");

      if (typeof this.desktopMediaQuery.addEventListener === "function") {
        this.desktopMediaQuery.addEventListener("change", this.updatePlacement);
      } else if (typeof this.desktopMediaQuery.addListener === "function") {
        this.desktopMediaQuery.addListener(this.updatePlacement);
      }

      this.updatePlacement();
    }

    updatePlacement = () => {
      if (!this.desktopMediaQuery) return;

      const targetSlot = this.desktopMediaQuery.matches ? this.desktopSlot : this.mobileSlot;

      if (targetSlot && this.parentElement !== targetSlot) {
        targetSlot.appendChild(this);
      }
    };

    handleSubmit = (event) => {
      event.preventDefault();
      window.clearTimeout(this.inputTimer);
      const query = this.input.value.trim();

      if (!query) {
        this.clearSearch();
        return;
      }

      if (query.length < this.minCharacters) {
        this.showMinimumCharactersMessage();
        return;
      }

      this.saveQuery(query);
      this.search(query);
    };

    handleInput = () => {
      window.clearTimeout(this.inputTimer);
      const query = this.input.value.trim();
      this.toggleClearButton(Boolean(query));

      if (!query) {
        this.clearSearch();
        return;
      }

      this.saveQuery(query);

      if (query.length < this.minCharacters) {
        this.restoreInitialProducts();
        this.showMinimumCharactersMessage();
        return;
      }

      this.inputTimer = window.setTimeout(() => this.search(query), 350);
    };

    handleClear = () => {
      this.input.value = "";
      this.input.focus();
      this.clearSearch();
    };

    saveQuery(query) {
      if (query) {
        stateStore[this.storageKey] = query;
      } else {
        delete stateStore[this.storageKey];
      }
    }

    clearSearch() {
      this.requestId += 1;
      this.saveQuery("");
      this.toggleClearButton(false);
      this.setLoading(false);
      this.restoreInitialProducts();
      this.setStatus("");
    }

    restoreInitialProducts() {
      if (!this.grid) return;

      this.grid.replaceChildren(...this.initialItems.map((item) => item.cloneNode(true)));
      this.grid.hidden = false;
      if (this.pagination) this.pagination.hidden = false;
      if (this.emptyState) this.emptyState.hidden = true;
      this.section?.classList.remove("m-collection-search-active");
      this.refreshProductCards();
    }

    showMinimumCharactersMessage() {
      const noun = this.minCharacters === 1 ? "character" : "characters";
      this.setStatus(`Enter at least ${this.minCharacters} ${noun} to search.`);
    }

    async search(rawQuery) {
      const query = rawQuery.trim();
      if (!query || query.length < this.minCharacters) return;

      if (!this.grid) {
        this.setStatus("There are no products available to search.");
        return;
      }

      const requestId = ++this.requestId;
      this.setLoading(true);
      this.setStatus("Searching products…");

      try {
        const items = await this.loadAllCollectionItems();
        if (requestId !== this.requestId) return;

        const tokens = this.normalize(query).split(" ").filter(Boolean);
        const matches = items.filter((item) => {
          const searchableText = this.normalize(item.dataset.collectionSearchText || item.textContent || "");
          return tokens.every((token) => searchableText.includes(token));
        });

        this.renderMatches(matches);
        const resultWord = matches.length === 1 ? "product found" : "products found";
        this.setStatus(`${matches.length} ${resultWord}`);
      } catch (error) {
        console.error("Collection search failed:", error);
        if (requestId === this.requestId) {
          this.restoreInitialProducts();
          this.setStatus("Unable to search this collection right now. Please try again.");
        }
      } finally {
        if (requestId === this.requestId) this.setLoading(false);
      }
    }

    renderMatches(matches) {
      const renderedItems = matches.map((item) => item.cloneNode(true));
      this.grid.replaceChildren(...renderedItems);
      this.grid.hidden = matches.length === 0;
      if (this.pagination) this.pagination.hidden = true;
      if (this.emptyState) this.emptyState.hidden = matches.length !== 0;
      this.section?.classList.add("m-collection-search-active");
      this.refreshProductCards();
    }

    loadAllCollectionItems() {
      if (this.allItemsPromise) return this.allItemsPromise;
      if (this.totalPages <= 1 || typeof window.fetchSection !== "function") {
        return Promise.resolve(this.allItems);
      }

      this.allItemsPromise = this.fetchAllPages();
      return this.allItemsPromise;
    }

    async fetchAllPages() {
      const pages = Array.from({ length: this.totalPages }, (_, index) => index + 1);
      const pageItems = new Array(this.totalPages);
      const currentPageIndex = Math.min(Math.max(this.currentPage, 1), this.totalPages) - 1;
      pageItems[currentPageIndex] = this.initialItems;

      const pagesToFetch = pages.filter((page) => page !== this.currentPage);
      const baseUrl = new URL(window.location.href);
      baseUrl.searchParams.delete("page");
      baseUrl.searchParams.delete("section_id");

      let cursor = 0;
      const workerCount = Math.min(4, pagesToFetch.length);
      const workers = Array.from({ length: workerCount }, async () => {
        while (cursor < pagesToFetch.length) {
          const page = pagesToFetch[cursor++];
          const sectionHtml = await window.fetchSection(this.sectionId, {
            url: baseUrl.toString(),
            fromCache: true,
            params: { page },
          });

          pageItems[page - 1] = Array.from(
            sectionHtml.querySelectorAll("[data-product-container] [data-collection-search-item]")
          ).map((item) => item.cloneNode(true));
        }
      });

      await Promise.all(workers);

      const seenProducts = new Set();
      this.allItems = pageItems.flat().filter((item) => {
        if (!item) return false;
        const productId = item.dataset.productId;
        if (!productId) return true;
        if (seenProducts.has(productId)) return false;
        seenProducts.add(productId);
        return true;
      });

      return this.allItems;
    }

    normalize(value) {
      return String(value)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();
    }

    toggleClearButton(show) {
      if (this.clearButton) this.clearButton.hidden = !show;
    }

    setLoading(loading) {
      this.classList.toggle("is-loading", loading);
      this.setAttribute("aria-busy", loading ? "true" : "false");
      if (this.submitButton) this.submitButton.disabled = loading;
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }

    refreshProductCards() {
      if (typeof window.handleBackgroundImageLazyload === "function") {
        window.handleBackgroundImageLazyload();
      }
      if (window.MinimogTheme?.Collection?.initProducts) {
        window.MinimogTheme.Collection.initProducts();
      }
      if (window.MinimogEvents?.emit) {
        window.MinimogEvents.emit("ON_PRODUCT_LIST_UPDATED");
      }
    }
  }

  if (!customElements.get("collection-search")) {
    customElements.define("collection-search", CollectionSearch);
  }
})();
